import type { CSSProperties, ReactElement } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createTranslator, positionLabel } from "../../i18n";
import { DomCardArt, skinUsesRocamborSprites } from "../../lib/dom-card-art";
import type { Card, SeatIndex } from "../../protocol";
import type { AppContext } from "../../router";
import type { ClientState } from "../../state";
import { useClientState, useSettings } from "../hooks";
import type { GameDomLayerBridge, GameDomLayerSnapshot } from "./game-dom-layer-bridge";
import { activeSeatsForRole, nextActiveSeat } from "./game-seat-utils";
import { seatAccentVars } from "./player-accent";
import { useGameDomLayerSnapshot } from "./useGameDomLayerSnapshot";

const TRICK_OVERLAY_LINGER_MS = 3200;

type VarStyle = CSSProperties & Record<string, string>;

function detectTouchConfirm(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: none)").matches;
}

function trickActorLabel(state: ClientState, seat: number | undefined, locale: "en" | "es"): string {
  if (seat === undefined) return "";
  const rel = state.relativePosition(seat as SeatIndex);
  if (rel === "self") return locale === "es" ? "TÚ" : "YOU";
  const game = state.game;
  let relLabel = positionLabel(rel, locale).toUpperCase();
  if (game?.ombre !== null && game?.ombre !== undefined) {
    if (seat === game.ombre) {
      relLabel = locale === "es" ? "JUGADOR" : "PLAYER";
    } else {
      const primer = nextActiveSeat(state, game.ombre);
      const segundo = nextActiveSeat(state, primer);
      if (seat === primer) relLabel = locale === "es" ? "PRIMER" : "FIRST";
      else if (seat === segundo) relLabel = locale === "es" ? "SEGUNDO" : "SECOND";
    }
  }
  const handle = state.game?.players[seat]?.handle;
  return handle ? `${relLabel} · ${handle}` : relLabel;
}

function trickSlotStyle(
  position: "self" | "left" | "across" | "right",
  isMobilePortrait: boolean
): VarStyle {
  const map = isMobilePortrait
    ? {
        left: { x: "-112px", y: "8px", r: "-7deg" },
        across: { x: "0px", y: "-104px", r: "0deg" },
        right: { x: "112px", y: "8px", r: "7deg" },
        self: { x: "0px", y: "44px", r: "0deg" },
      }
    : {
        left: { x: "-214px", y: "18px", r: "-10deg" },
        across: { x: "0px", y: "-90px", r: "0deg" },
        right: { x: "214px", y: "18px", r: "10deg" },
        self: { x: "0px", y: "18px", r: "0deg" },
      };
  const slot = map[position];
  return {
    "--slot-x": slot.x,
    "--slot-y": slot.y,
    "--slot-rot": slot.r,
  };
}

function handFanStyle(index: number, count: number, isMobilePortrait: boolean): VarStyle | undefined {
  if (isMobilePortrait) return undefined;
  const mid = (count - 1) / 2;
  const delta = index - mid;
  const spread = Math.min(22, 120 / Math.max(4, count));
  const rotate = delta * spread * 0.52;
  const x = delta * 3.2;
  const y = Math.abs(delta) * 2.2;
  return {
    "--fan-rot": `${rotate.toFixed(2)}deg`,
    "--fan-x": `${x.toFixed(2)}px`,
    "--fan-y": `${y.toFixed(2)}px`,
  };
}

export function GameTrickDomLayers({
  ctx,
  bridge,
}: {
  ctx: AppContext;
  bridge: GameDomLayerBridge;
}): ReactElement {
  const state = useClientState(ctx.state);
  const settings = useSettings(ctx.settings);
  const snapshot = useGameDomLayerSnapshot(bridge);
  const { t } = createTranslator(settings.locale);
  const game = state.game;

  // Sticky overlay: the bridge overlay can be briefly null during React batched
  // re-renders (state update clears table before overlay snapshot propagates).
  // We hold the overlay in local state and only clear it via a timed effect.
  const [stickyOverlay, setStickyOverlay] = useState(snapshot.trickDisplayOverlay);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (snapshot.trickDisplayOverlay) {
      setStickyOverlay(snapshot.trickDisplayOverlay);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setStickyOverlay(null);
        timerRef.current = null;
      }, TRICK_OVERLAY_LINGER_MS);
    }
    // Don't cancel the timer when overlay goes null — that's exactly when
    // we need the sticky state to hold. Only cancel on unmount.
  }, [snapshot.trickDisplayOverlay]);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  const overlay = snapshot.trickDisplayOverlay ?? stickyOverlay;

  const trickCards = game?.table.length
    ? game.table
    : overlay?.cards ?? [];
  const trickOrder = game?.table.length
    ? game.playOrder
    : overlay?.playOrder ?? [];
  const trickWinner = game?.table.length
    ? null
    : overlay?.winner ?? null;
  return (
    <div id="game-dom-layers" className="game-dom-layers" hidden={!snapshot.spriteMode}>
      <div className="trick-overlay" aria-hidden="true">
        <div className="trick-overlay-inner" id="trick-layer">
          {trickCards.map((card, index) => {
            const seat = trickOrder[index];
            const rel = seat === undefined ? "across" : state.relativePosition(seat);
            const isWinner = trickWinner !== null && seat === trickWinner;
            return (
              <div
                key={`${card.id}-${index}`}
                className={`trick-card-wrap${isWinner ? " winner" : ""}`}
                style={{
                  ...trickSlotStyle(rel, snapshot.isMobilePortrait),
                  ...(seat !== undefined ? seatAccentVars(seat as SeatIndex) : {}),
                }}
              >
                {isWinner ? <div className="trick-winner-badge">{t("game.winner")}</div> : null}
                <DomCardArt
                  card={card}
                  skinId={settings.cardSkin}
                  colorblind={settings.colorblindMode}
                />
                {seat !== undefined ? (
                  <div className="trick-card-label">{trickActorLabel(state, seat, settings.locale)}</div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function resolveVolteoRevealCard(
  state: ClientState,
  snapshot: GameDomLayerSnapshot
): Card | null {
  const game = state.game;
  if (game?.phase === "exchange" && !game.exchange.revealedCard) {
    return null;
  }
  const canShowVolteoReveal =
    game?.contract === "volteo" &&
    game?.phase !== "play" &&
    game?.phase !== "scoring" &&
    game?.phase !== "post_hand" &&
    game?.phase !== "match_end" &&
    game?.phase !== "lobby";
  return (
    (canShowVolteoReveal ? game?.exchange?.revealedCard : null) ??
    (canShowVolteoReveal ? snapshot.volteoRevealCard : null) ??
    null
  );
}

export function GameVolteoReveal({
  ctx,
  bridge,
}: {
  ctx: AppContext;
  bridge: GameDomLayerBridge;
}): ReactElement | null {
  const state = useClientState(ctx.state);
  const settings = useSettings(ctx.settings);
  const snapshot = useGameDomLayerSnapshot(bridge);
  const { t } = createTranslator(settings.locale);
  const volteoRevealCard = resolveVolteoRevealCard(state, snapshot);

  if (!volteoRevealCard) return null;

  return (
    <div className="volteo-reveal-wrap" aria-hidden="true">
      <div className="volteo-reveal-meta">
        <div className="volteo-reveal-title">{t("game.volteoRevealTitle")}</div>
      </div>
      <div className="volteo-reveal-card">
        <DomCardArt
          card={volteoRevealCard}
          skinId={settings.cardSkin}
          colorblind={settings.colorblindMode}
        />
      </div>
    </div>
  );
}

export function GameHandDock({
  ctx,
  bridge,
}: {
  ctx: AppContext;
  bridge: GameDomLayerBridge;
}): ReactElement {
  const state = useClientState(ctx.state);
  const settings = useSettings(ctx.settings);
  const snapshot = useGameDomLayerSnapshot(bridge);
  const handLayerRef = useRef<HTMLDivElement | null>(null);
  const dragSuppressUntilRef = useRef(0);
  const dragStateRef = useRef({
    pointerId: null as number | null,
    startX: 0,
    startScrollLeft: 0,
    dragging: false,
  });
  const touchConfirm = useMemo(() => detectTouchConfirm(), []);
  const handSignature = state.hand.map((card) => card.id).join("|");
  const { t } = createTranslator(settings.locale);
  const usesSpriteSheet = skinUsesRocamborSprites(settings.cardSkin);

  useEffect(() => {
    if (!snapshot.spriteMode || state.hand.length === 0 || !usesSpriteSheet) return;

    const timeoutId = window.setTimeout(() => {
      const nodes = handLayerRef.current?.querySelectorAll<HTMLElement>(".roc-card") ?? [];
      if (!nodes.length) {
        bridge.reportSpriteRenderFailure();
        return;
      }

      const sample = Array.from(nodes).slice(0, 3);
      const renderable = sample.every((node) => {
        const style = window.getComputedStyle(node);
        const bg = style.backgroundImage || "";
        const width = parseFloat(style.width || "0");
        const height = parseFloat(style.height || "0");
        return bg.includes("url(") && !bg.includes("none") && width > 0 && height > 0;
      });

      if (!renderable) {
        bridge.reportSpriteRenderFailure();
      }
    }, 140);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    bridge,
    handSignature,
    snapshot.spriteMode,
    snapshot.isMobilePortrait,
    state.hand.length,
    usesSpriteSheet,
  ]);

  useEffect(() => {
    const row = handLayerRef.current;
    if (!row || !snapshot.isMobilePortrait) return;

    const drag = dragStateRef.current;

    const beginDrag = (clientX: number): void => {
      drag.startX = clientX;
      drag.startScrollLeft = row.scrollLeft;
      drag.dragging = false;
    };

    const updateDrag = (clientX: number): boolean => {
      const delta = clientX - drag.startX;
      if (!drag.dragging && Math.abs(delta) > 6) {
        drag.dragging = true;
        row.classList.add("dragging");
      }
      if (!drag.dragging) return false;
      row.scrollLeft = drag.startScrollLeft - delta;
      return true;
    };

    const finishDrag = (pointerId: number): void => {
      if (drag.pointerId !== pointerId) return;
      if (drag.dragging) {
        dragSuppressUntilRef.current = Date.now() + 220;
      }
      drag.pointerId = null;
      drag.dragging = false;
      row.classList.remove("dragging");
      try {
        if (row.hasPointerCapture(pointerId)) {
          row.releasePointerCapture(pointerId);
        }
      } catch {
        // Some browsers can reject pointer capture release after a cancel.
      }
    };

    const handlePointerDown = (event: PointerEvent): void => {
      if (row.scrollWidth <= row.clientWidth + 4) return;
      if (event.pointerType === "touch") return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      drag.pointerId = event.pointerId;
      beginDrag(event.clientX);
      try {
        row.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is a progressive enhancement for smoother drag scrolling.
      }
    };

    const handlePointerMove = (event: PointerEvent): void => {
      if (drag.pointerId !== event.pointerId) return;
      if (updateDrag(event.clientX)) {
        event.preventDefault();
      }
    };

    const handlePointerUp = (event: PointerEvent): void => {
      finishDrag(event.pointerId);
    };

    const handlePointerCancel = (event: PointerEvent): void => {
      finishDrag(event.pointerId);
    };

    row.addEventListener("pointerdown", handlePointerDown);
    row.addEventListener("pointermove", handlePointerMove);
    row.addEventListener("pointerup", handlePointerUp);
    row.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      row.removeEventListener("pointerdown", handlePointerDown);
      row.removeEventListener("pointermove", handlePointerMove);
      row.removeEventListener("pointerup", handlePointerUp);
      row.removeEventListener("pointercancel", handlePointerCancel);
      row.classList.remove("dragging");
      drag.pointerId = null;
      drag.dragging = false;
    };
  }, [snapshot.isMobilePortrait, handSignature]);

  const game = state.game;
  const legalIds = game?.legalIds || [];
  const handleHandKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();

    const row = e.currentTarget;
    const buttons = Array.from(row.querySelectorAll<HTMLButtonElement>("button.hand-card-wrap:not([disabled])"));
    if (buttons.length === 0) return;

    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);

    let nextIndex: number;
    if (e.key === "ArrowLeft") {
      nextIndex = currentIndex <= 0 ? buttons.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex >= buttons.length - 1 ? 0 : currentIndex + 1;
    }

    buttons[nextIndex]?.focus();
  }, []);

  const showHandDock = snapshot.spriteMode && state.hand.length > 0;

  return (
    <div className="game-hand-dock" id="game-hand-dock" aria-label={t("game.yourHandArea")} hidden={!showHandDock}>
      <div className="hand-dock-header" aria-hidden="true">
        <span className="hand-dock-title">{t("game.yourHand")}</span>
        {touchConfirm && state.game?.phase === "play" && state.isMyTurn && snapshot.pendingPlayCard !== null && (
          <span className="hand-dock-confirm-hint" aria-live="polite">
            {t("game.tapToConfirm")}
          </span>
        )}
        {(!snapshot.isMobilePortrait || state.hand.length > 6) && (
          <span className="hand-dock-hint">
            {snapshot.isMobilePortrait ? t("game.swipeHintMobile") : t("game.swipe")}
          </span>
        )}
      </div>
      <div
        key={snapshot.invalidShakeNonce}
        ref={handLayerRef}
        className={`hand-row${snapshot.invalidShakeNonce > 0 ? " invalid-shake" : ""}`}
        id="hand-layer"
        role="listbox"
        aria-label="Your hand"
        onKeyDown={handleHandKeyDown}
      >
        {state.hand.map((card, index) => {
          const selected = state.selectedCards.has(card.id);
          const isPlay = state.phase === "play" && state.isMyTurn;
          const illegal = isPlay && legalIds.length > 0 && !legalIds.includes(card.id);
          const legal = isPlay && !illegal;
          const pending = touchConfirm && snapshot.pendingPlayCard === card.id;

          return (
            <button
              key={card.id}
              className={`hand-card-wrap${selected ? " selected" : ""}${illegal ? " illegal" : ""}${legal ? " legal" : ""}${pending ? " pending" : ""}`}
              type="button"
              role="option"
              aria-selected={selected}
              data-card-id={card.id}
              style={handFanStyle(index, state.hand.length, snapshot.isMobilePortrait)}
              disabled={illegal}
              onClick={() => {
                if (Date.now() < dragSuppressUntilRef.current) return;
                navigator.vibrate?.(6);
                bridge.interactWithCard(card.id, snapshot.isMobilePortrait ? false : touchConfirm);
              }}
            >
              <DomCardArt
                card={card}
                skinId={settings.cardSkin}
                colorblind={settings.colorblindMode}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
